import type { ModelInfo } from "./types.js";

export interface CatalogEntry extends ModelInfo {
  family: string;
  maker: string;
  parameterSizes: string[];
  activeParams?: string;
  architecture: "dense" | "moe";
  quantizations: { name: string; sizeGB: number }[];
  minVramGB: number;
  minRamGB: number;
  benchmarks?: {
    liveCodeBench?: number;
    humaneval?: number;
    mmlu?: number;
    sweBench?: number;
    codeforcesElo?: number;
  };
  recommendedFor: ("coding" | "reasoning" | "chat" | "agents" | "edge" | "fast" | "cheap")[];
  ollamaTags: string[];
  ollamaCloud?: boolean;
  openWeight: boolean;
  released: string;
}

export const MODEL_CATALOG: CatalogEntry[] = [
  {
    id: "gemma4:31b",
    name: "Gemma 4 31B",
    family: "gemma4",
    maker: "Google DeepMind",
    provider: "ollama",
    contextWindow: 262144,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: true,
    supportsThinking: true,
    parameterSizes: ["31B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 20 }],
    minVramGB: 22,
    minRamGB: 32,
    benchmarks: { liveCodeBench: 80.0, mmlu: 85.2, sweBench: 52.0, codeforcesElo: 2150 },
    recommendedFor: ["coding", "reasoning", "agents"],
    ollamaTags: ["gemma4:31b", "gemma4:latest"],
    ollamaCloud: true,
    openWeight: true,
    released: "2026-04",
    tags: ["gemma4", "31b", "dense"],
  },
  {
    id: "gemma4:26b",
    name: "Gemma 4 26B MoE",
    family: "gemma4",
    maker: "Google DeepMind",
    provider: "ollama",
    contextWindow: 262144,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: true,
    supportsThinking: true,
    parameterSizes: ["26B"],
    activeParams: "3.8B",
    architecture: "moe",
    quantizations: [{ name: "Q4_K_M", sizeGB: 18 }],
    minVramGB: 20,
    minRamGB: 28,
    benchmarks: { liveCodeBench: 77.1, mmlu: 82.6, sweBench: 48.0, codeforcesElo: 1718 },
    recommendedFor: ["coding", "reasoning", "agents", "fast"],
    ollamaTags: ["gemma4:26b"],
    openWeight: true,
    released: "2026-04",
    tags: ["gemma4", "26b", "moe"],
  },
  {
    id: "gemma4:e4b",
    name: "Gemma 4 E4B",
    family: "gemma4",
    maker: "Google DeepMind",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: true,
    supportsThinking: true,
    parameterSizes: ["8B"],
    activeParams: "4.5B",
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 9.6 }],
    minVramGB: 11,
    minRamGB: 16,
    benchmarks: { liveCodeBench: 52.0, mmlu: 69.4, codeforcesElo: 940 },
    recommendedFor: ["coding", "edge", "fast", "cheap"],
    ollamaTags: ["gemma4:e4b"],
    openWeight: true,
    released: "2026-04",
    tags: ["gemma4", "e4b", "edge"],
  },
  {
    id: "gemma4:e2b",
    name: "Gemma 4 E2B",
    family: "gemma4",
    maker: "Google DeepMind",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: true,
    supportsThinking: true,
    parameterSizes: ["5.1B"],
    activeParams: "2.3B",
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 7.2 }],
    minVramGB: 8,
    minRamGB: 12,
    benchmarks: { liveCodeBench: 44.0, mmlu: 60.0, codeforcesElo: 633 },
    recommendedFor: ["edge", "fast", "cheap"],
    ollamaTags: ["gemma4:e2b"],
    openWeight: true,
    released: "2026-04",
    tags: ["gemma4", "e2b", "edge"],
  },
  {
    id: "qwen3-coder:30b",
    name: "Qwen3-Coder 30B",
    family: "qwen3-coder",
    maker: "Alibaba",
    provider: "ollama",
    contextWindow: 262144,
    maxOutputTokens: 16384,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: false,
    parameterSizes: ["30B"],
    activeParams: "3.3B",
    architecture: "moe",
    quantizations: [{ name: "Q4_K_M", sizeGB: 19 }],
    minVramGB: 21,
    minRamGB: 28,
    benchmarks: { liveCodeBench: 78.0, humaneval: 90.0, sweBench: 55.0 },
    recommendedFor: ["coding", "agents"],
    ollamaTags: ["qwen3-coder:30b", "qwen3-coder"],
    openWeight: true,
    released: "2025-10",
    tags: ["qwen3", "coder", "30b", "moe"],
  },
  {
    id: "qwen3-coder:480b",
    name: "Qwen3-Coder 480B",
    family: "qwen3-coder",
    maker: "Alibaba",
    provider: "ollama",
    contextWindow: 262144,
    maxOutputTokens: 16384,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: false,
    parameterSizes: ["480B"],
    architecture: "moe",
    quantizations: [{ name: "Q4_K_M", sizeGB: 290 }],
    minVramGB: 320,
    minRamGB: 384,
    recommendedFor: ["coding", "agents"],
    ollamaTags: ["qwen3-coder:480b"],
    ollamaCloud: true,
    openWeight: true,
    released: "2025-10",
    tags: ["qwen3", "coder", "480b", "moe"],
  },
  {
    id: "qwen3:32b",
    name: "Qwen3 32B",
    family: "qwen3",
    maker: "Alibaba",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: true,
    parameterSizes: ["32B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 19 }],
    minVramGB: 21,
    minRamGB: 28,
    benchmarks: { liveCodeBench: 72.0, mmlu: 83.0, sweBench: 45.0 },
    recommendedFor: ["coding", "reasoning", "agents"],
    ollamaTags: ["qwen3:32b"],
    openWeight: true,
    released: "2025-04",
    tags: ["qwen3", "32b", "dense"],
  },
  {
    id: "qwen3:14b",
    name: "Qwen3 14B",
    family: "qwen3",
    maker: "Alibaba",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: true,
    parameterSizes: ["14B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 9 }],
    minVramGB: 11,
    minRamGB: 16,
    benchmarks: { liveCodeBench: 62.0, mmlu: 76.0, sweBench: 35.0 },
    recommendedFor: ["coding", "reasoning", "fast"],
    ollamaTags: ["qwen3:14b"],
    openWeight: true,
    released: "2025-04",
    tags: ["qwen3", "14b"],
  },
  {
    id: "qwen3:8b",
    name: "Qwen3 8B",
    family: "qwen3",
    maker: "Alibaba",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: true,
    parameterSizes: ["8B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 5 }],
    minVramGB: 6,
    minRamGB: 10,
    benchmarks: { liveCodeBench: 52.0, mmlu: 68.0 },
    recommendedFor: ["coding", "edge", "fast", "cheap"],
    ollamaTags: ["qwen3:8b"],
    openWeight: true,
    released: "2025-04",
    tags: ["qwen3", "8b"],
  },
  {
    id: "qwen3:4b",
    name: "Qwen3 4B",
    family: "qwen3",
    maker: "Alibaba",
    provider: "ollama",
    contextWindow: 32768,
    maxOutputTokens: 4096,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: true,
    parameterSizes: ["4B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 2.5 }],
    minVramGB: 4,
    minRamGB: 8,
    recommendedFor: ["edge", "fast", "cheap"],
    ollamaTags: ["qwen3:4b"],
    openWeight: true,
    released: "2025-04",
    tags: ["qwen3", "4b"],
  },
  {
    id: "qwen3.5:27b",
    name: "Qwen3.5 27B",
    family: "qwen3.5",
    maker: "Alibaba",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: true,
    supportsThinking: true,
    parameterSizes: ["27B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 16 }],
    minVramGB: 18,
    minRamGB: 24,
    recommendedFor: ["coding", "reasoning", "agents"],
    ollamaTags: ["qwen3.5:27b"],
    openWeight: true,
    released: "2026-03",
    tags: ["qwen3.5", "27b"],
  },
  {
    id: "deepseek-r1:14b",
    name: "DeepSeek-R1 14B",
    family: "deepseek-r1",
    maker: "DeepSeek",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 8192,
    supportsToolUse: false,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: true,
    parameterSizes: ["14B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 9 }],
    minVramGB: 11,
    minRamGB: 16,
    benchmarks: { mmlu: 78.0, humaneval: 85.0 },
    recommendedFor: ["reasoning", "coding"],
    ollamaTags: ["deepseek-r1:14b"],
    openWeight: true,
    released: "2025-01",
    tags: ["deepseek", "r1", "14b"],
  },
  {
    id: "deepseek-r1:32b",
    name: "DeepSeek-R1 32B",
    family: "deepseek-r1",
    maker: "DeepSeek",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 8192,
    supportsToolUse: false,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: true,
    parameterSizes: ["32B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 19 }],
    minVramGB: 21,
    minRamGB: 28,
    benchmarks: { mmlu: 84.0, humaneval: 89.0 },
    recommendedFor: ["reasoning", "coding"],
    ollamaTags: ["deepseek-r1:32b"],
    openWeight: true,
    released: "2025-01",
    tags: ["deepseek", "r1", "32b"],
  },
  {
    id: "deepseek-r1:70b",
    name: "DeepSeek-R1 70B",
    family: "deepseek-r1",
    maker: "DeepSeek",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 8192,
    supportsToolUse: false,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: true,
    parameterSizes: ["70B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 40 }],
    minVramGB: 44,
    minRamGB: 56,
    benchmarks: { mmlu: 88.0, humaneval: 92.0 },
    recommendedFor: ["reasoning", "coding"],
    ollamaTags: ["deepseek-r1:70b"],
    openWeight: true,
    released: "2025-01",
    tags: ["deepseek", "r1", "70b"],
  },
  {
    id: "devstral-small:24b",
    name: "Devstral Small 2 24B",
    family: "devstral",
    maker: "Mistral AI",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: true,
    supportsThinking: false,
    parameterSizes: ["24B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 14 }],
    minVramGB: 16,
    minRamGB: 22,
    benchmarks: { sweBench: 50.0, liveCodeBench: 70.0 },
    recommendedFor: ["coding", "agents"],
    ollamaTags: ["devstral-small-2", "devstral-small:24b"],
    openWeight: true,
    released: "2025-06",
    tags: ["devstral", "24b", "coding"],
  },
  {
    id: "glm-4.7-flash",
    name: "GLM-4.7-Flash",
    family: "glm",
    maker: "Z.ai",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: true,
    parameterSizes: ["30B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 18 }],
    minVramGB: 20,
    minRamGB: 26,
    benchmarks: { mmlu: 82.0, liveCodeBench: 72.0 },
    recommendedFor: ["coding", "reasoning", "agents"],
    ollamaTags: ["glm-4.7-flash"],
    openWeight: true,
    released: "2025-08",
    tags: ["glm", "30b"],
  },
  {
    id: "phi4:14b",
    name: "Phi-4 14B",
    family: "phi",
    maker: "Microsoft",
    provider: "ollama",
    contextWindow: 16384,
    maxOutputTokens: 4096,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: false,
    parameterSizes: ["14B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 9 }],
    minVramGB: 11,
    minRamGB: 16,
    benchmarks: { humaneval: 84.0, mmlu: 80.0 },
    recommendedFor: ["coding", "reasoning", "fast"],
    ollamaTags: ["phi4:14b", "phi4"],
    openWeight: true,
    released: "2025-01",
    tags: ["phi", "14b"],
  },
  {
    id: "phi4-mini:3.8b",
    name: "Phi-4 Mini 3.8B",
    family: "phi",
    maker: "Microsoft",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 4096,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: false,
    parameterSizes: ["3.8B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 2.4 }],
    minVramGB: 4,
    minRamGB: 8,
    benchmarks: { humaneval: 72.0, mmlu: 68.0 },
    recommendedFor: ["edge", "fast", "cheap", "coding"],
    ollamaTags: ["phi4-mini"],
    openWeight: true,
    released: "2025-02",
    tags: ["phi", "mini", "3.8b"],
  },
  {
    id: "llama4-scout:17b",
    name: "Llama 4 Scout 17B MoE",
    family: "llama4",
    maker: "Meta",
    provider: "ollama",
    contextWindow: 10485760,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: true,
    supportsThinking: false,
    parameterSizes: ["109B"],
    activeParams: "17B",
    architecture: "moe",
    quantizations: [{ name: "Q4_K_M", sizeGB: 65 }],
    minVramGB: 72,
    minRamGB: 88,
    benchmarks: { mmlu: 84.0, liveCodeBench: 68.0 },
    recommendedFor: ["coding", "reasoning", "agents"],
    ollamaTags: ["llama4-scout"],
    openWeight: true,
    released: "2025-04",
    tags: ["llama4", "scout", "moe"],
  },
  {
    id: "llama4-maverick:17b",
    name: "Llama 4 Maverick 17B MoE",
    family: "llama4",
    maker: "Meta",
    provider: "ollama",
    contextWindow: 1048576,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: true,
    supportsThinking: false,
    parameterSizes: ["400B"],
    activeParams: "17B",
    architecture: "moe",
    quantizations: [{ name: "Q4_K_M", sizeGB: 240 }],
    minVramGB: 260,
    minRamGB: 320,
    benchmarks: { mmlu: 88.0, liveCodeBench: 75.0 },
    recommendedFor: ["reasoning", "coding", "agents"],
    ollamaTags: ["llama4-maverick"],
    openWeight: true,
    released: "2025-04",
    tags: ["llama4", "maverick", "moe"],
  },
  {
    id: "mistral-small:24b",
    name: "Mistral Small 24B",
    family: "mistral",
    maker: "Mistral AI",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: false,
    parameterSizes: ["24B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 14 }],
    minVramGB: 16,
    minRamGB: 22,
    benchmarks: { mmlu: 78.0, liveCodeBench: 65.0 },
    recommendedFor: ["coding", "chat", "fast"],
    ollamaTags: ["mistral-small:24b"],
    openWeight: true,
    released: "2025-01",
    tags: ["mistral", "24b"],
  },
  {
    id: "codestral:22b",
    name: "Codestral 22B",
    family: "codestral",
    maker: "Mistral AI",
    provider: "ollama",
    contextWindow: 262144,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: false,
    parameterSizes: ["22B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 13 }],
    minVramGB: 15,
    minRamGB: 20,
    benchmarks: { humaneval: 86.0, liveCodeBench: 66.0 },
    recommendedFor: ["coding", "agents"],
    ollamaTags: ["codestral:22b", "codestral"],
    openWeight: true,
    released: "2025-05",
    tags: ["codestral", "22b", "coding"],
  },
  {
    id: "nemotron-cascade-2:30b",
    name: "Nemotron Cascade 2 30B",
    family: "nemotron",
    maker: "NVIDIA",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: true,
    parameterSizes: ["30B"],
    activeParams: "3B",
    architecture: "moe",
    quantizations: [{ name: "Q4_K_M", sizeGB: 18 }],
    minVramGB: 20,
    minRamGB: 26,
    recommendedFor: ["reasoning", "agents", "fast"],
    ollamaTags: ["nemotron-cascade-2"],
    openWeight: true,
    released: "2026-03",
    tags: ["nemotron", "30b", "moe"],
  },
  {
    id: "lfm2:24b",
    name: "LFM2 24B MoE",
    family: "lfm2",
    maker: "Liquid AI",
    provider: "ollama",
    contextWindow: 32768,
    maxOutputTokens: 4096,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: false,
    parameterSizes: ["24B"],
    activeParams: "4B",
    architecture: "moe",
    quantizations: [{ name: "Q4_K_M", sizeGB: 14 }],
    minVramGB: 16,
    minRamGB: 20,
    recommendedFor: ["edge", "fast", "coding"],
    ollamaTags: ["lfm2:24b", "lfm2"],
    openWeight: true,
    released: "2025-09",
    tags: ["lfm2", "24b", "moe"],
  },
  {
    id: "gpt-oss:20b",
    name: "GPT-OSS 20B",
    family: "gpt-oss",
    maker: "OpenAI",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 16384,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: true,
    parameterSizes: ["20B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 12 }],
    minVramGB: 14,
    minRamGB: 20,
    recommendedFor: ["reasoning", "coding", "agents"],
    ollamaTags: ["gpt-oss:20b", "gpt-oss"],
    openWeight: true,
    released: "2025-08",
    tags: ["gpt-oss", "20b"],
  },
  {
    id: "gpt-oss:120b",
    name: "GPT-OSS 120B",
    family: "gpt-oss",
    maker: "OpenAI",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 16384,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: true,
    parameterSizes: ["120B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 72 }],
    minVramGB: 80,
    minRamGB: 96,
    recommendedFor: ["reasoning", "coding", "agents"],
    ollamaTags: ["gpt-oss:120b"],
    ollamaCloud: true,
    openWeight: true,
    released: "2025-08",
    tags: ["gpt-oss", "120b"],
  },
  {
    id: "qwen3:30b-a3b",
    name: "Qwen3 30B-A3B MoE",
    family: "qwen3",
    maker: "Alibaba",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 8192,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: true,
    parameterSizes: ["30B"],
    activeParams: "3B",
    architecture: "moe",
    quantizations: [{ name: "Q4_K_M", sizeGB: 17 }],
    minVramGB: 19,
    minRamGB: 24,
    benchmarks: { liveCodeBench: 65.0, mmlu: 80.0 },
    recommendedFor: ["coding", "fast", "agents"],
    ollamaTags: ["qwen3:30b-a3b"],
    openWeight: true,
    released: "2025-04",
    tags: ["qwen3", "30b", "moe"],
  },
  {
    id: "ministral-3b",
    name: "Ministral 3B",
    family: "mistral",
    maker: "Mistral AI",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 4096,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: true,
    supportsThinking: false,
    parameterSizes: ["3B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 2 }],
    minVramGB: 3,
    minRamGB: 6,
    recommendedFor: ["edge", "fast", "cheap"],
    ollamaTags: ["ministral-3b"],
    openWeight: true,
    released: "2025-06",
    tags: ["mistral", "3b", "edge"],
  },
  {
    id: "rnj-1:8b",
    name: "Rnj-1 8B",
    family: "rnj",
    maker: "Essential AI",
    provider: "ollama",
    contextWindow: 32768,
    maxOutputTokens: 4096,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: false,
    parameterSizes: ["8B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 5 }],
    minVramGB: 6,
    minRamGB: 10,
    recommendedFor: ["coding", "fast", "cheap"],
    ollamaTags: ["rnj-1:8b"],
    openWeight: true,
    released: "2025-09",
    tags: ["rnj", "8b"],
  },
  {
    id: "olmo-3.1:32b",
    name: "Olmo 3.1 32B",
    family: "olmo",
    maker: "Allen AI",
    provider: "ollama",
    contextWindow: 131072,
    maxOutputTokens: 4096,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: false,
    supportsThinking: false,
    parameterSizes: ["32B"],
    architecture: "dense",
    quantizations: [{ name: "Q4_K_M", sizeGB: 19 }],
    minVramGB: 21,
    minRamGB: 28,
    recommendedFor: ["coding", "reasoning"],
    ollamaTags: ["olmo-3.1:32b"],
    openWeight: true,
    released: "2025-10",
    tags: ["olmo", "32b"],
  },
  {
    id: "claude-sonnet-4-20250514",
    name: "Claude Sonnet 4",
    family: "claude",
    maker: "Anthropic",
    provider: "anthropic",
    contextWindow: 200000,
    maxOutputTokens: 16384,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: true,
    supportsThinking: true,
    parameterSizes: ["proprietary"],
    architecture: "dense",
    quantizations: [],
    minVramGB: 0,
    minRamGB: 0,
    benchmarks: { sweBench: 72.0, liveCodeBench: 88.0, mmlu: 90.0 },
    recommendedFor: ["coding", "reasoning", "agents"],
    ollamaTags: [],
    openWeight: false,
    released: "2025-05",
    tags: ["claude", "sonnet", "cloud"],
  },
  {
    id: "gpt-4o",
    name: "GPT-4o",
    family: "gpt",
    maker: "OpenAI",
    provider: "openai",
    contextWindow: 128000,
    maxOutputTokens: 16384,
    supportsToolUse: true,
    supportsStreaming: true,
    supportsVision: true,
    supportsThinking: false,
    parameterSizes: ["proprietary"],
    architecture: "dense",
    quantizations: [],
    minVramGB: 0,
    minRamGB: 0,
    benchmarks: { sweBench: 65.0, liveCodeBench: 85.0, mmlu: 88.0 },
    recommendedFor: ["coding", "reasoning", "agents"],
    ollamaTags: [],
    openWeight: false,
    released: "2024-05",
    tags: ["gpt", "4o", "cloud"],
  },
];
